/**
 * Where each tour got to, and whether to offer the welcome pop-up.
 *
 * The account is the record (`backend/tours.py`): it decides who is new, and
 * only one tab ever hears "offer it". Without a database the same rules run
 * here on `localStorage`, so the pop-up shows once per browser (rule 1).
 * `localStorage` is also written when there is a database, as a mirror, so
 * the tours list shows the right state at once.
 */

import { Atom } from '../../state/atom.ts';
import {
  answerTourWelcome,
  claimTourWelcome,
  getTourProgress,
  putTourProgress,
  type TourProgressRow,
} from '../../api/endpoints.ts';

const KEY = 'tours.progress.v1';
const MAX_OFFERS = 2;
const ASK_AGAIN_MS = 3 * 24 * 3600 * 1000;

interface LocalRow extends TourProgressRow {
  offers?: number;
}

export const tourProgress = new Atom<Record<string, LocalRow>>(readLocal());
/** Whether the account keeps the progress (a database), or only this browser. */
let stored = false;

function readLocal(): Record<string, LocalRow> {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, LocalRow>) : {};
  } catch {
    return {};
  }
}

function writeLocal(next: Record<string, LocalRow>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private window or blocked storage: the tour still runs, it is just not remembered here.
  }
}

function setRow(id: string, row: Partial<LocalRow>): void {
  const prev = tourProgress.value[id];
  const next = {
    ...tourProgress.value,
    [id]: {
      state: 'started',
      step: null,
      step_count: null,
      version: null,
      ...prev,
      ...row,
      updated_at: new Date().toISOString(),
    },
  };
  tourProgress.set(next);
  writeLocal(next);
}

/** Read the account's copy. The account wins over this browser's mirror. */
export async function loadTourProgress(): Promise<void> {
  try {
    const res = await getTourProgress();
    stored = res.stored;
    if (stored) {
      tourProgress.set(res.progress);
      writeLocal(res.progress);
    }
  } catch {
    stored = false;
  }
}

/**
 * Should this tab show the welcome pop-up? Asks the account when there is one.
 * Never throws: a failed request means "do not ask", not an error on screen.
 */
export async function claimWelcome(): Promise<boolean> {
  try {
    const res = await claimTourWelcome();
    stored = res.stored;
    if (res.stored) {
      if (res.offer) setRow('welcome', { state: 'offered' });
      return res.offer;
    }
  } catch {
    return false;
  }
  // No database: the same rules, on this browser.
  const row = tourProgress.value['welcome'];
  const offers = row?.offers ?? 0;
  const waited = row?.updated_at ? Date.now() - Date.parse(row.updated_at) > ASK_AGAIN_MS : true;
  const offer = !row || ((row.state === 'later' || row.state === 'offered') && offers < MAX_OFFERS && waited);
  if (offer) setRow('welcome', { state: 'offered', offers: offers + 1 });
  return offer;
}

export function answerWelcome(answer: 'accepted' | 'later' | 'declined'): void {
  setRow('welcome', { state: answer });
  if (stored) void answerTourWelcome(answer).catch(() => undefined);
}

export function recordTour(
  id: string,
  state: 'started' | 'finished' | 'stopped',
  step: number,
  stepCount: number,
  version: number,
): void {
  const finishedAt = tourProgress.value[id]?.finished_at;
  setRow(id, {
    state,
    step,
    step_count: stepCount,
    version,
    finished_at: finishedAt ?? (state === 'finished' ? new Date().toISOString() : null),
  });
  if (stored) {
    void putTourProgress(id, { state, step, step_count: stepCount, version }).catch(() => undefined);
  }
}

/** True once the tour was finished, also when it was started again later. */
export function everFinished(id: string): boolean {
  const row = tourProgress.value[id];
  return !!row && (row.state === 'finished' || !!row.finished_at);
}
